import { useEffect, useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

const NAV_ITEMS = [
  { to: "/", label: "首页" },
  { to: "/archive", label: "往期存档" },
  { to: "/about", label: "关于我们" },
  { to: "/faq", label: "常见问题" },
];

export default function SiteHeader() {
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setSearchOpen(false);
  }, [location.pathname]);

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    const value = keyword.trim();
    navigate(value ? `/archive?q=${encodeURIComponent(value)}` : "/archive");
    setSearchOpen(false);
    setKeyword("");
  };

  return (
    <header
      className={`sticky top-0 z-50 w-full border-b transition-colors duration-300 ${
        scrolled
          ? "border-background-200 bg-background-50/90 backdrop-blur-md"
          : "border-transparent bg-background-50/60 backdrop-blur-sm"
      }`}
    >
      <div className="mx-auto flex h-16 w-full items-center gap-4 px-4 md:h-20 md:px-8">
        <Link to="/" className="flex items-center gap-2.5 whitespace-nowrap" aria-label="看料88 首页">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-base font-bold text-background-50 md:h-10 md:w-10">
            看
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-heading text-lg font-bold tracking-tight text-foreground-950 md:text-xl">
              看料88
            </span>
            <span className="hidden text-[11px] tracking-wider text-foreground-500 sm:block">
              往期热料存档
            </span>
          </span>
        </Link>

        <nav className="ml-6 hidden items-center gap-1 md:flex" aria-label="主导航">
          {NAV_ITEMS.map((item) => {
            const active = item.to === "/" ? location.pathname === "/" : location.pathname.startsWith(item.to);
            return (
              <Link
                key={item.to}
                to={item.to}
                className={`rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                  active
                    ? "bg-primary-50 text-primary-700"
                    : "text-foreground-700 hover:bg-background-100 hover:text-foreground-950"
                }`}
              >
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="ml-auto flex items-center gap-2">
          <form
            onSubmit={submitSearch}
            className={`hidden items-center overflow-hidden rounded-full border border-background-300 bg-background-100 transition-all duration-300 lg:flex ${
              searchOpen ? "w-56" : "w-9"
            }`}
          >
            <button
              type={searchOpen ? "submit" : "button"}
              onClick={() => !searchOpen && setSearchOpen(true)}
              aria-label="搜索往期内容"
              className="flex h-9 w-9 shrink-0 items-center justify-center text-foreground-600 hover:text-primary-600"
            >
              <i className="ri-search-line text-base" />
            </button>
            {searchOpen && (
              <input
                autoFocus
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                onBlur={() => !keyword && setSearchOpen(false)}
                placeholder="搜索往期热料"
                className="h-9 w-full bg-transparent pr-3 text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
              />
            )}
          </form>

          <Link
            to="/about"
            className="hidden whitespace-nowrap rounded-full bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 md:block"
          >
            订阅更新
          </Link>

          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label="打开导航菜单"
            aria-expanded={menuOpen}
            className="flex h-9 w-9 items-center justify-center rounded-md border border-background-300 text-foreground-800 md:hidden"
          >
            <i className={menuOpen ? "ri-close-line text-lg" : "ri-menu-line text-lg"} />
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="border-t border-background-200 bg-background-50 md:hidden">
          <nav className="flex flex-col px-4 py-3" aria-label="移动端导航">
            {NAV_ITEMS.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className="rounded-md px-3 py-3 text-sm font-medium text-foreground-800 hover:bg-background-100"
              >
                {item.label}
              </Link>
            ))}
            <form onSubmit={submitSearch} className="mt-2 flex items-center gap-2 rounded-md bg-background-100 px-3">
              <i className="ri-search-line text-foreground-500" />
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索往期热料"
                className="h-11 w-full bg-transparent text-sm outline-none placeholder:text-foreground-400"
              />
            </form>
          </nav>
        </div>
      )}
    </header>
  );
}